import { throwError as observableThrowError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { EnvironmentService } from '../../services/environment.service';
import { ErrorReportingService } from '../../services/error-reporting.service';
import { RepData, UnitListResponse, UserData } from '../../models/admin-models';
import { Profile } from '../../models/profile';
import { GridDataResponse } from '../../models/post-trade-models';

@Injectable({
  providedIn: 'root',
})
export class UniversalSearchService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private router = inject(Router);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  private baseUrl: string;
  private app: string;
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.rbs.baseUrl;
    this.org = this.environment.org;
    this.app = this.ss.get('currentApp') || this.environment.defaultApp || 'pp';
    this.org = this.environment.org || 'basev2';
  }

  userSearch(search?, unit?): Observable<{ names: UserData[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    let url = this.baseUrl + '/Hierarchy/User?searchString=';

    if (search) {
      url += search;
    } else {
      url += '%25';
    }

    if (unit) {
      url += '&UnitID=' + unit;
    }

    return this.http
      .get<{ names: UserData[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(() => error.error || 'Server error');
        })
      );
  }

  repCodeSearch(
    search?,
    unit?,
    maxRows = 20
  ): Observable<{ names: RepData[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    let url = this.baseUrl + '/Hierarchy/RepCode?searchString=';

    if (search) {
      url += search;
    } else {
      url += '%25';
    }

    if (unit) {
      url += '&UnitID=' + unit;
    }

    url += '&maxRows=' + maxRows;

    return this.http
      .get<{ names: RepData[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(() => error.error || 'Server error');
        })
      );
  }

  getSuggestions() {
    const sess = this.ss.get('session');

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = this.baseUrl + '/Search/Bridges';

    return this.http.get(url, { headers: reqHeaders }).pipe(
      map(res => {
        const data = res;
        const suggs = {};
        data['bridges'].map(z => {
          const cat = z.Category;

          if (cat && suggs[cat]) {
            suggs[cat].push({ id: z.ID, name: z.Name, category: cat });
          } else if (cat) {
            suggs[cat] = [];
            suggs[cat].push({ id: z.ID, name: z.Name, category: cat });
          }
        });

        return suggs;
      }),
      catchError((error: any) => {
        return observableThrowError(() => error.error || 'Server error');
      })
    );
  }

  profileSearch(type, unified = false): Observable<{ data: Profile[] }> {
    if (!this.baseUrl) {
      this.environment = this.envSvc.get();
      this.baseUrl = this.environment.apiBase;
    }

    this.app = unified ? 'rb' : this.ss.get('currentApp');
    const sess = this.ss.get('session');
    const app = this.app === 'summary' ? 'pp' : this.app;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    const url = this.baseUrl + '/Search/ProfileSearch';

    const stringData = encodeURIComponent(type);
    const body = `searchCriteria=${stringData}`;

    return this.http
      .post<{ data: Profile[] }>(url, body.toString(), { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(() => error.error || 'Server error');
        })
      );
  }

  caseSearch(data, responseType?): Observable<GridDataResponse | Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', responseType);
    const url = this.baseUrl + '/Search/CaseSearch';

    if (responseType === 'application/xlsx') {
      return this.http
        .post(url, data, {
          headers: reqHeaders,
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(
          map(res => {
            return res.body;
          }),
          catchError((error: any) => {
            return this.errSvc.handleError(error);
          })
        );
    } else {
      return this.http.post(url, data, { headers: reqHeaders }).pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
    }
  }

  nameSearch(srch): Observable<{ names: { first: string; last: string }[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Search/NameSearch?searchString=${srch}&unitId=%&userId=%`;

    return this.http
      .get<{
        names: { first: string; last: string }[];
      }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(() => error.error || 'Server error');
        })
      );
  }

  excelAddress(ids): Observable<HttpResponse<Blob>> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Reports/Address`;
    const data = { profileIds: ids };

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(() => error.error || 'Server error');
        })
      );
  }
}
